import NextAuth, { CredentialsSignin, User } from 'next-auth';
import CredentialsProvider from 'next-auth/providers/credentials';

import { RUser } from '@dh/types';
import { postToCMS } from '@dh/api';

import { getLogger } from '../logger/logger';
import { Endpoint } from '../../constants';

const logger = getLogger('nextauth');

export class UnconfirmedError extends CredentialsSignin {
    code = 'unconfirmed';
}

export class NotApprovedError extends CredentialsSignin {
    code = 'not_approved';
}

export const { handlers, signIn, signOut, auth } = NextAuth({
    providers: [
        CredentialsProvider({
            name: 'Strapi Login',
            id: 'strapiLogin',
            type: 'credentials',
            credentials: {
                email: { type: 'text' },
                password: { type: 'password' }
            },
            authorize: async (credentials) => {
                if (!credentials) {
                    logger.error('No Credentials provided.');
                    return null;
                }

                const { email, password } = credentials;

                const response = await postToCMS<{
                    user: RUser;
                    jwt: string;
                }>({
                    endpoint: Endpoint.LOGIN,
                    body: JSON.stringify({
                        identifier: email,
                        password
                    }),
                    jwt: process.env.STRAPI_API_TOKEN ?? ''
                });

                if (!response.data || response.error) {
                    logger.error(response.error, 'Login failed.');
                    if (response.error?.details?.includes('email is not confirmed')) {
                        throw new UnconfirmedError('Email is not confirmed');
                    }
                    return null;
                }

                const { id, documentId, hasApproval } = response.data.user;

                if (!hasApproval) {
                    logger.error(response.error, 'Login failed.');

                    throw new NotApprovedError('The user is not approved yet');
                }

                // as defined in User interface in next-auth.d.ts
                const user: User = {
                    id: id.toString(),
                    documentId,
                    jwt: response.data.jwt
                };

                return user;
            }
        })
    ],
    secret: process.env.SECRET,
    session: {
        strategy: 'jwt',
        maxAge: 1 * 60 * 60 * 24 // 1 day
    },
    callbacks: {
        async session({ session, token }) {
            return { ...session, user: token.user as User };
        },
        async jwt({ token, user }) {
            if (user) token.user = user;
            return token;
        }
    },
    pages: {
        signIn: '/login',
        signOut: '/logout',
        error: '/login'
    },
    debug: process.env.APP_ENV === 'development'
});
